import { DevtoolsProvider, DevtoolsPanel } from "@refinedev/devtools";
import { Authenticated, Refine } from "@refinedev/core";
import { KBarProvider } from "@refinedev/kbar";
import {
  ErrorComponent,
  useNotificationProvider,
  RefineSnackbarProvider,
} from "@refinedev/mui";
import GlobalStyles from "@mui/material/GlobalStyles";
import CssBaseline from "@mui/material/CssBaseline";
import routerProvider, {
  CatchAllNavigate,
  NavigateToResource,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router";
import { BrowserRouter, Routes, Route, Outlet } from "react-router";
import { useTranslation } from "react-i18next";
import {
  DashboardOutlined,
  Business,
  AccountCircleOutlined,
  ImageOutlined,
  InventoryOutlined,
  MessageOutlined,
  ShoppingCartOutlined,
  LocalOfferOutlined,
  GroupsOutlined,
  SettingsOutlined,
  LockPersonOutlined,
  VideoLibraryOutlined
} from "@mui/icons-material";
import Box from "@mui/material/Box";
import { authProvider } from "./authProvider";
import { DashboardPage } from "./pages/dashboard";
import { ContactShow, ContactList, ContactCreateEdit } from "./pages/contacts";
import { AuthPage } from "./pages/auth";
import { ProductList, ProductAddEditForm } from "./pages/products";
import { CollectionAddEditForm } from "./pages/collections";
import { ColorModeContextProvider } from "./contexts";
// import { Header, Title } from "./components";
import { SuspenseAndErrorBoundary } from "./components/security";
import { Media } from "./pages/medias";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "./utils/data-provider/tanstackQueryClient";
import { data_provider } from "./utils";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { CompanyList, CompanyCreateEdit, CompanyShow } from "./pages/companies";
import { BroadcastPage, BroadcastCreateEdit } from "./pages/broadcast";
import { OrderList } from "./pages/orders";
import { CreateEditDeal, DealList } from "./pages/deals";
import { lazy, Suspense } from 'react';
import { CircularProgress } from "@mui/material";
import SareeIcon from './assests/icons/SareeIcon';
import { PostHogProvider } from 'posthog-js/react'
import posthog from 'posthog-js';
import { TeamMemberList, TeamMemberCreateEdit } from "./pages/team-members";
import CouponIcon from "./assests/icons/Coupon";
import { CouponList } from "./pages/coupons";
import CreateEditCoupon from "./pages/coupons/create-edit";
import GeneralSettings from "./pages/general-settings";
import { ThemedSiderV2 } from "./components/layout/sider";
import { ThemedLayoutV2 } from "./components/layout";
import { ThemedHeaderV2 } from "./components/layout/header";
import { ThemedTitleV2 } from "./components/layout/title";
import { RolePermissionList } from "./pages/role-permission";
import VideoList from "./pages/videos/list";

// Initialize PostHog
posthog.init(import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY || '', {
  api_host: import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
  debug: Boolean(import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_DEBUG),
});
const ProductShow = lazy(() => import('./pages/products/show'));
const CollectionList = lazy(() => import('./pages/collections/list'));
const CollectionShow = lazy(() => import('./pages/collections/show'));

const LoadingComponent = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    height="100vh"
  >
    <CircularProgress />
  </Box>
);

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  return (
    <PostHogProvider client={posthog}>
      <BrowserRouter>
        <KBarProvider>
          <ColorModeContextProvider>
            <CssBaseline />
            <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
            <RefineSnackbarProvider>
              <QueryClientProvider client={queryClient}>
                <DevtoolsProvider url="http://localhost:5001">
                  <DndProvider backend={HTML5Backend}>
                    <Refine
                      routerProvider={routerProvider}
                      dataProvider={data_provider}
                      authProvider={authProvider}
                      i18nProvider={i18nProvider}
                      options={{
                        syncWithLocation: true,
                        warnWhenUnsavedChanges: true,
                        breadcrumb: true,
                        useNewQueryKeys: true,
                        mutationMode: "optimistic",
                        projectId: "wLBKVP-Z3Vepb-63fc5g"
                      }}
                      notificationProvider={useNotificationProvider}
                      resources={[
                        {
                          name: "dashboard",
                          list: "/",
                          meta: {
                            label: "Dashboard",
                            icon: <DashboardOutlined />,
                          },
                        },
                        {
                          name: "accounts",
                          list: "/accounts",
                          create: "/accounts/new",
                          edit: "/accounts/:id/edit",
                          show: "/accounts/:id",
                          meta: {
                            label: "Contacts",
                            icon: <AccountCircleOutlined />,
                            canDelete: true,
                          },
                        },
                        {
                          name: "companies",
                          list: "/companies",
                          show: "/companies/:id",
                          edit: "/companies/:id/edit",
                          create: "/companies/new",
                          meta: {
                            label: "Companies",
                            icon: <Business />,
                            canDelete: true,
                          },
                        },
                        {
                          name: "products",
                          list: "/products",
                          create: "/products/new",
                          edit: "/products/:id/edit",
                          show: "/products/:id",
                          meta: {
                            icon: <SareeIcon />,
                            canDelete: true,
                          },
                        },
                        {
                          name: "collections",
                          list: "/collections",
                          create: "/collections/new",
                          edit: "/collections/:id/edit",
                          show: "/collections/:id",
                          meta: {
                            icon: <InventoryOutlined />,
                            canDelete: true,
                          },
                        },
                        {
                          name: "deals",
                          list: "/deals",
                          create: "/deals/new",
                          edit: "/deals/:id/edit",
                          meta: {
                            icon: <LocalOfferOutlined />,
                            canDelete: true,
                          },
                        },
                        {
                          name: "cart-coupons",
                          list: "/cart-coupons",
                          create: "/cart-coupons/new",
                          edit: "/cart-coupons/:id/edit",
                          meta: {
                            icon: <CouponIcon />,
                            canDelete: true,
                          },
                        },
                        {
                          name: "medias",
                          list: "/medias",
                          meta: {
                            icon: <ImageOutlined />,
                          },
                        },
                        {
                          name: "rooms/broadcasts",
                          list: "/rooms/broadcasts",
                          create: "/rooms/broadcasts/new",
                          edit: "/rooms/broadcasts/:id/edit",
                          meta: {
                            label: "Broadcasts",
                            icon: <MessageOutlined />,
                          },
                        },
                        {
                          name: "orders",
                          list: "/orders",
                          meta: {
                            icon: <ShoppingCartOutlined />,
                          },
                        },
                        {
                          name: "accounts/team-members",
                          list: "/accounts/team-members",
                          create: "/accounts/team-members/new",
                          edit: "/accounts/team-members/:id/edit",
                          meta: {
                            label: "Team Members",
                            icon: <GroupsOutlined />,
                          }
                        },
                        {
                          name: "general-settings",
                          list: "/general-settings",
                          edit: "/general-settings/:id",
                          meta: {
                            label: "Settings",
                            icon: <SettingsOutlined />,
                          }
                        },
                        {
                          name: "role_permissions",
                          list: "/role_permissions",
                          meta: {
                            label: "Role Permissions",
                            icon: <LockPersonOutlined />,
                          },
                        },
                        {
                          name: "videos",
                          list: "/videos",
                          meta: {
                            label: "Demo Videos",
                            icon: <VideoLibraryOutlined />,
                          },
                        }
                      ]}
                    >
                      <Routes>
                        <Route
                          element={
                            <Authenticated
                              key="authenticated-routes"
                              fallback={<CatchAllNavigate to="/login" />}
                            >
                              <ThemedLayoutV2 Header={ThemedHeaderV2} Title={ThemedTitleV2} Sider={ThemedSiderV2}>
                                <Box
                                  sx={{
                                    maxWidth: "1200px",
                                    marginLeft: "auto",
                                    marginRight: "auto",
                                  }}
                                >
                                  <SuspenseAndErrorBoundary>
                                    <Outlet />
                                  </SuspenseAndErrorBoundary>
                                </Box>
                              </ThemedLayoutV2>
                            </Authenticated>
                          }
                        >
                          <Route index element={<DashboardPage />} />
                          <Route path="dummy-dashboard" element={<DashboardPage />} />
                          <Route index element={<NavigateToResource resource="products" />} />

                          <Route path="/accounts" >
                            <Route index element={<ContactList />} />
                            <Route path=":id" element={<ContactShow />} />
                            <Route path="new" element={<ContactCreateEdit actionType="create" />} />
                            <Route path=":id/edit" element={<ContactCreateEdit actionType="edit" />} />
                          </Route>

                          <Route path="/products" >
                            <Route index element={<ProductList />} />
                            <Route path=":id/edit" element={<ProductAddEditForm actionType="edit" />} />
                            <Route path="new" element={<ProductAddEditForm actionType="create" />} />
                            <Route path=":id" element={<ProductShow />} />
                          </Route>

                          <Route path="/collections" >
                            <Route index element={
                              <Suspense fallback={<LoadingComponent />}>
                                <CollectionList >
                                  <Outlet />
                                </CollectionList>
                              </Suspense>
                            } />
                            <Route path="new" element={<CollectionAddEditForm actionType="create" />} />
                            <Route path=":id/edit" element={<CollectionAddEditForm actionType="edit" />} />
                            <Route path=":id" element={<CollectionShow />} />
                          </Route>

                          <Route path="/companies" >
                            <Route index element={<CompanyList />} />
                            <Route path="new" element={<CompanyCreateEdit actionType="create" />} />
                            <Route path=":id/edit" element={<CompanyCreateEdit actionType="edit" />} />
                            <Route path=":id" element={<CompanyShow />} />
                          </Route>

                          <Route path="/deals" >
                            <Route index element={<DealList />} />
                            <Route path="new" element={<CreateEditDeal actionType="create" />} />
                            <Route path=":id/edit" element={<CreateEditDeal actionType="edit" />} />
                          </Route>

                          <Route path="/cart-coupons" >
                            <Route index element={<CouponList />} />
                            <Route path="new" element={<CreateEditCoupon actionType="create" />} />
                            <Route path=":id/edit" element={<CreateEditCoupon actionType="edit" />} />
                          </Route>

                          <Route path="/medias">
                            <Route index element={<Media />} />
                            {/* <Route path="show" element={<MediaShow />} /> */}
                          </Route>

                          <Route path="/rooms/broadcasts">
                            <Route index element={<BroadcastPage />} />
                            <Route path="new" element={<BroadcastCreateEdit actionType="create" />} />
                            <Route path=":id/edit" element={<BroadcastCreateEdit actionType="edit" />} />
                          </Route>

                          <Route path="/orders">
                            <Route index element={<OrderList />} />
                          </Route>

                          <Route path="/accounts/team-members">
                            <Route index element={<TeamMemberList />} />
                            <Route path="new" element={<TeamMemberCreateEdit actionType="create" />} />
                            <Route path=":id/edit" element={<TeamMemberCreateEdit actionType="edit" />} />
                          </Route>

                          <Route path="/general-settings">
                            <Route index element={<GeneralSettings />} />
                          </Route>

                          <Route path="/role_permissions">
                            <Route index element={<RolePermissionList />} />
                          </Route>

                          <Route path="/videos">
                            <Route index element={<VideoList />} />
                          </Route>
                        </Route>


                        <Route
                          element={
                            <Authenticated key="auth-pages" fallback={<Outlet />}>
                              <NavigateToResource resource="dashboard" />
                            </Authenticated>
                          }
                        >
                          <Route path="/login" element={<AuthPage />} />
                        </Route>

                        <Route
                          element={
                            <Authenticated key="catch-all">
                              <ThemedLayoutV2 Header={ThemedHeaderV2} Title={ThemedTitleV2}>
                                <Outlet />
                              </ThemedLayoutV2>
                            </Authenticated>
                          }
                        >
                          <Route path="*" element={<ErrorComponent />} />
                        </Route>
                      </Routes>
                      <UnsavedChangesNotifier />
                      {/* Title of the tab resource name or label + Kayaan Prints Admin Dashboard */}
                      <DocumentTitleHandler handler={(options) =>
                        `${options.resource && (options.resource.meta?.label || options.resource.name)
                          ? `${(options.resource?.meta?.label || options.resource?.name || "").charAt(0).toUpperCase() + (options.resource?.meta?.label || options.resource?.name || "").slice(1)} | `
                          : ""
                        }Kayaan Prints Admin Dashboard`
                      }
                      />
                    </Refine>
                  </DndProvider>
                  <DevtoolsPanel />
                </DevtoolsProvider>
              </QueryClientProvider>
            </RefineSnackbarProvider>
          </ColorModeContextProvider>
        </KBarProvider>
      </BrowserRouter>
    </PostHogProvider>
  );
};

export default App;